// Full-document render of the tempurpedic ProductCategoryListPage:
// PageSkeleton wrapping the body (normal site chrome; no body_class / style
// overrides):
//
// 1. The extra_copy header (server-rendered HTML: heading -> slugurl-linked
//    h1, paragraph -> rich-text div; the extra_copy loop has no branch for
//    the image/html block types, so they stay dropped).
// 2. The 2-level category side nav. The mobile "Shop All" toggle owns its
//    collapse state here, toggling `accordion--is-active` on itself and
//    `accordion-target--is-active` on the sibling primary ul (no `aria-hidden`
//    on that ul: it stays visible on desktop regardless of the mobile-only
//    collapse). This nesting is intentional: the secondary (level-3) <ul>
//    renders as a SIBLING of its parent's <li>, not nested inside it.
// 3. The routed content area: subcat routes render the product-card grids
//    (single category -> h3 title + always-rendered list div; multiple ->
//    per-category div title + optional raw hero-image tile + grid); the
//    default route renders the category-card tiles.
//
// The extra `category_noindex` robots meta stacks on the base
// show_in_site_search one (both can emit at once); React 19 hoists it into
// <head>.
import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { templates } from "@reactivated";

import { PageSkeleton } from "../components/PageSkeleton";
import { ProductCard } from "../components/ProductCard";

// Mutable reactivated props (not Immutable<>): this template composes the
// in-tree SiteHeader chain via PageSkeleton, which is typed with mutable
// reactivated props (see PageSkeleton).
type IProps = templates.ProductCategoryListPageTemplate;

const CategoryCard = (
    props: Immutable<{ card: IProps["category_cards"][number] }>,
): React.JSX.Element => {
    const { card } = props;
    // Hoisted for the ttag template below (member expressions in `t`
    // interpolations break `ttag update` extraction).
    const cardTitle = card.title;
    return (
        <a href={card.link} className="card category-card" title={card.title}>
            {card.image_url && (
                <img
                    alt={t`${cardTitle} category product image`}
                    src={card.image_url}
                    className="card__image category-card__image responsive-img"
                    loading="lazy"
                    decoding="async"
                />
            )}
            <div className="card__content category-card__content">
                {card.title && (
                    <h4 className="card__title category-card__title">
                        {card.title}
                    </h4>
                )}
                {card.description_html && (
                    <div
                        className="card__description category-card__description"
                        dangerouslySetInnerHTML={{
                            __html: card.description_html,
                        }}
                    />
                )}
            </div>
        </a>
    );
};

const SubCategoryContent = (props: {
    contentIsSingle: boolean;
    sections: IProps["subcat_sections"];
}): React.JSX.Element => {
    if (props.contentIsSingle) {
        // The server always builds exactly one section on the single branch.
        const section = props.sections[0];
        return (
            <>
                <h3 className="category-list-sub-category__title">
                    {section.title}
                </h3>
                <div className="category-list-sub-category__list u-flex-container">
                    {section.cards.map((card) => (
                        <ProductCard
                            key={card.product_id}
                            card={card}
                            cssClass="sub-category-card"
                            auxCopy={true}
                        />
                    ))}
                </div>
            </>
        );
    }
    return (
        <>
            {props.sections.map((section, i) => (
                <React.Fragment key={i}>
                    <div className="category-list-sub-category__title">
                        {section.title}
                    </div>
                    <div className="category-list-sub-category__list u-flex-container">
                        {section.hero_image_url && (
                            <div
                                className="card product-card sub-category-card sub-category-card__hero-image"
                                style={{
                                    backgroundImage: `url(${section.hero_image_url})`,
                                }}
                            ></div>
                        )}
                        {section.cards.map((card) => (
                            <ProductCard
                                key={card.product_id}
                                card={card}
                                cssClass="sub-category-card"
                                auxCopy={true}
                            />
                        ))}
                    </div>
                </React.Fragment>
            ))}
        </>
    );
};

export const Template = (props: IProps): React.JSX.Element => {
    const [sideNavExpanded, setSideNavExpanded] = React.useState(false);
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            {props.category_noindex && <meta name="robots" content="noindex" />}
            <div className="l-full-width u-margin-bottom--xl">
                <div className="category-list category-list--misc-products u-full-width-title">
                    <div className="category-list-header l-capped-width u-margin-bottom--l">
                        <div
                            className="category-list-header__copy"
                            dangerouslySetInnerHTML={{
                                __html: props.header_copy_html,
                            }}
                        />
                    </div>
                    <div className="l-capped-width">
                        <div className="l-side-nav category-list-side-nav">
                            <div
                                className={
                                    sideNavExpanded
                                        ? "category-list-side-nav__mobile-toggle accordion accordion--is-active"
                                        : "category-list-side-nav__mobile-toggle accordion"
                                }
                                role="button"
                                tabIndex={0}
                                aria-expanded={
                                    sideNavExpanded ? "true" : "false"
                                }
                                onClick={() => {
                                    setSideNavExpanded((value) => !value);
                                }}
                                onKeyDown={(event) => {
                                    if (
                                        event.key === "Enter" ||
                                        event.key === " "
                                    ) {
                                        event.preventDefault();
                                        setSideNavExpanded((value) => !value);
                                    }
                                }}
                            >
                                {props.shop_all_label}
                            </div>
                            <ul
                                className={
                                    sideNavExpanded
                                        ? "category-list-side-nav__list category-list-side-nav__list--primary accordion-target accordion-target--is-active"
                                        : "category-list-side-nav__list category-list-side-nav__list--primary accordion-target"
                                }
                            >
                                {props.nav_items.map((item, i) => (
                                    <React.Fragment key={i}>
                                        <li className="category-list-side-nav__item">
                                            <a
                                                className="category-list-side-nav__link"
                                                href={item.url}
                                            >
                                                {item.name}
                                            </a>
                                        </li>
                                        {item.children.length > 0 && (
                                            <ul className="category-list-side-nav__list category-list-side-nav__list--secondary">
                                                {item.children.map(
                                                    (child, j) => (
                                                        <li
                                                            key={j}
                                                            className="category-list-side-nav__item category-list-side-nav__item--secondary"
                                                        >
                                                            <a
                                                                className="category-list-side-nav__link category-list-side-nav__link--secondary"
                                                                href={child.url}
                                                            >
                                                                {child.name}
                                                            </a>
                                                        </li>
                                                    ),
                                                )}
                                            </ul>
                                        )}
                                    </React.Fragment>
                                ))}
                            </ul>
                        </div>
                        {props.has_category ? (
                            <section className="category-list-content category-list-content--expanded">
                                <SubCategoryContent
                                    contentIsSingle={props.content_is_single}
                                    sections={props.subcat_sections}
                                />
                            </section>
                        ) : (
                            <section className="l-side-nav-content category-list-content u-flex-container l-capped-width">
                                {props.category_cards.map((card, i) => (
                                    <CategoryCard key={i} card={card} />
                                ))}
                            </section>
                        )}
                    </div>
                </div>
            </div>
        </PageSkeleton>
    );
};
